import { API_PATHS } from "../../config";
import { normalizeApiListResponse } from "../../utils/common/helpers";
import { apiRequest } from "./axios";

/**
 * RFI (Request for Information) API functions
 * Functions for managing RFI tickets
 */

// ===== Core CRUD Functions =====

/**
 * List all RFIs for the log table.
 *
 * @returns {Promise<Array|Object>}
 */
export async function getRFIs() {
  const url = "/rfis/";
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Slim RFI rows for Projects Home / Project Overview.
 *
 * @param {Object} [options]
 * @param {number|string} [options.jobId] Optional job PK to scope the list.
 * @returns {Promise<Array<Object>>}
 */
export async function getRFIProjectSummaries({ jobId } = {}) {
  const params = new URLSearchParams();
  if (jobId != null && jobId !== "") {
    params.set("job", String(jobId));
  }
  const query = params.toString();
  const url = `${API_PATHS.RFI.PROJECT_SUMMARIES}${query ? `?${query}` : ""}`;
  const data = await apiRequest(url, null, { method: "GET" });
  return normalizeApiListResponse(data);
}

/**
 * Fetch one RFI by primary key.
 *
 * @param {number|string} id RFI PK.
 * @returns {Promise<Object>}
 */
export async function getRFIById(id) {
  const url = `/rfis/${id}/`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Create an RFI.
 *
 * @param {Object} data RFI create payload.
 * @returns {Promise<Object>}
 */
export async function createRFI(data) {
  const url = "/rfis/";
  return await apiRequest(url, data, { method: "POST" });
}

/**
 * Patch an existing RFI.
 *
 * @param {number|string} id RFI PK.
 * @param {Object} data Partial RFI fields.
 * @returns {Promise<Object>}
 */
export async function updateRFI(id, data) {
  const url = `/rfis/${id}/`;
  return await apiRequest(url, data, { method: "PATCH" });
}

/**
 * Delete an RFI.
 *
 * @param {number|string} id RFI PK.
 * @returns {Promise<Object>}
 */
export async function deleteRFI(id) {
  const url = `/rfis/${id}/`;
  return await apiRequest(url, null, { method: "DELETE" });
}

// ===== Status Actions (from TicketViewSetMixin) =====

/**
 * Toggle the locked flag on an RFI (TicketViewSetMixin action).
 *
 * @param {number|string} id RFI PK.
 * @returns {Promise<Object>}
 */
export async function toggleRFILocked(id) {
  const url = `/rfis/${id}/toggle-locked/`;
  return await apiRequest(url, {}, { method: "POST" });
}

// ===== RFI-Specific Functions =====

/**
 * Next available RFI ID for a job number (any string, e.g. "668-05").
 *
 * @param {string|number} jobNumber Job number as stored on the RFI.
 * @returns {Promise<Object>}
 */
export async function getNextRFIId(jobNumber) {
  const url = `/rfis/next-id/?job_number=${encodeURIComponent(String(jobNumber))}`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Add an internal user response to an RFI.
 *
 * @param {number|string} id RFI PK.
 * @param {string} response Response text.
 * @returns {Promise<Object>}
 */
export async function addRFIResponse(id, response) {
  const url = `/rfis/${id}/add-response/`;
  return await apiRequest(url, { response }, { method: "POST" });
}

/**
 * Update RFI workflow status, optionally attaching files and emailing.
 *
 * @param {number|string} id RFI PK.
 * @param {string} status New status value.
 * @param {string[]} [attachmentFilenames=[]] Attachment names to include.
 * @param {boolean} [sendEmail=false] Whether to send the status-change email.
 * @returns {Promise<Object>}
 */
export async function updateRFIStatus(id, status, attachmentFilenames = [], sendEmail = false) {
  const url = `/rfis/${id}/update-status/`;
  const body = { status };
  if (attachmentFilenames && attachmentFilenames.length > 0) {
    body.attachment_filenames = attachmentFilenames;
  }
  if (sendEmail) {
    body.send_email = true;
  }
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * PM direct answer: write a response and close the RFI.
 *
 * @param {number|string} id RFI PK.
 * @param {string} response Answer text.
 * @returns {Promise<Object>}
 */
export async function answerAndCloseRFI(id, response) {
  const url = `/rfis/${id}/answer-and-close/`;
  return await apiRequest(url, { response }, { method: "POST" });
}

/**
 * Forward an RFI to an external party (GC/owner).
 *
 * @param {number|string} id RFI PK.
 * @param {string|null} [notes=null] Optional forward notes.
 * @returns {Promise<Object>}
 */
export async function forwardRFI(id, notes = null) {
  const url = `/rfis/${id}/forward/`;
  const body = notes ? { notes } : {};
  return await apiRequest(url, body, { method: "POST" });
}

/**
 * Resolve the attachment folder path for an RFI.
 *
 * @param {number|string} rfiId RFI PK.
 * @returns {Promise<Object>}
 */
export async function getRFIAttachmentFolderPath(rfiId) {
  const url = `/rfis/${rfiId}/attachment-folder-path/`;
  return await apiRequest(url, null, { method: "GET" });
}

/**
 * Send an RFI to the owner.
 *
 * @param {number|string} id RFI PK.
 * @param {Object} data Send-to-owner payload.
 * @returns {Promise<Object>}
 */
export async function sendRFIToOwner(id, data) {
  const url = `/rfis/${id}/send-to-owner/`;
  return await apiRequest(url, data, { method: "POST" });
}

/**
 * Append a chat message on an RFI.
 *
 * @param {number|string} id RFI PK.
 * @param {string} message Chat text.
 * @returns {Promise<Object>}
 */
export async function addRFIChatMessage(id, message) {
  const url = `/rfis/${id}/add-chat-message/`;
  return await apiRequest(url, { message }, { method: "POST" });
}